import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useAction } from "wasp/client/operations";
import { getFacilityById, deleteFacility } from "wasp/client/operations";
import { toast } from "../../../hooks/use-toast";
import { useAuth } from "wasp/client/auth";
import {
  ArrowLeft,
  MapPin,
  Building2,
  Coffee,
  Monitor,
  Users,
  User,
  Edit3,
  Trash2,
  Eye,
  Clock,
  Tag,
  Share2,
  Calendar,
  Phone,
  Mail,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ExternalLink,
} from "lucide-react";
import { SubscribeButton } from "../../content/components/SubscribeButton";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger,
} from "../../../components/layout";
import { DashboardLayout } from "../../../client/components/Sidebar";
import { useLocationContext } from "../../../client/contexts/LocationContext";

export default function ViewFacilityPage() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteFacilityAction = useAction(deleteFacility);

  // Fetch facility data
  const {
    data: facility,
    isLoading,
    error,
  } = useQuery(getFacilityById, { facilityId: facilityId! });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "vending":
        return <Coffee className="w-5 h-5" />;
      case "equipment":
        return <Monitor className="w-5 h-5" />;
      case "rooms":
        return <Building2 className="w-5 h-5" />;
      case "coworking":
        return <Users className="w-5 h-5" />;
      default:
        return <MapPin className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "vending":
        return "text-orange-600 bg-orange-50 border-orange-200";
      case "equipment":
        return "text-green-600 bg-green-50 border-green-200";
      case "rooms":
        return "text-purple-600 bg-purple-50 border-purple-200";
      case "coworking":
        return "text-blue-600 bg-blue-50 border-blue-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "available":
        return "text-green-600 bg-green-50 border-green-200";
      case "reservable":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "walkin":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/facilities`);
    } else {
      navigate("/facilities");
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600">
              You must be logged in to view facilities.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading facility...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !facility) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Facility Not Found
            </h3>
            <p className="text-gray-600 mb-4">
              The facility you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Facilities
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isAvailable = facility.metadata?.isAvailable !== false;
  const isAdmin = user?.isAdmin;

  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "About",
      dropdownItems: [
        {
          label: "Notices",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/notices`
            : "/notices",
        },
        {
          label: "Events/Activities",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/events`
            : "/events",
        },
        {
          label: "Tenants",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/tenants`
            : "/tenants",
        },
        {
          label: "Facilities",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/facilities`
            : "/facilities",
        },
      ],
    },
    {
      label: "Facilities",
      isClickable: true,
      href: selectedLocation?.locationCode
        ? `/locations/${selectedLocation.locationCode}/facilities`
        : "/facilities",
    },
  ];

  const sidebarContent = (
    <>
      {isAdmin && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButtonLink
              to={`/facilities/${facility.id}/edit`}
              icon={Edit3}
            >
              Edit Facility
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={async () => {
                if (confirm("Are you sure you want to delete this facility?")) {
                  try {
                    await deleteFacilityAction({ facilityId: facility.id });
                    toast({ title: "Success", description: "Facility deleted successfully" });
                    navigate(selectedLocation?.locationCode ? `/locations/${selectedLocation.locationCode}/facilities` : "/facilities");
                  } catch (err: any) {
                    toast({ title: "Error", description: err.message || "Failed to delete facility", variant: "destructive" });
                  }
                }
              }}
              icon={Trash2}
            >
              Delete Facility
            </ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Category" value={facility.category || "N/A"} />
          <DetailField
            label="Type"
            value={
              facility.metadata?.facilityType === "available"
                ? "Available"
                : facility.metadata?.facilityType === "reservable"
                  ? "Reservable"
                  : facility.metadata?.facilityType === "walkin"
                    ? "Walk-in Only"
                    : "N/A"
            }
          />
          <DetailField
            label="Status"
            value={isAvailable ? "Available" : "Unavailable"}
          />
          <DetailField
            label="Added by"
            value={
              facility.author?.username || facility.author?.email || "Anonymous"
            }
          />
          <DetailField label="Created" value={formatDate(facility.createdAt)} />
          <DetailField
            label="Location"
            value={facility.location?.name || "N/A"}
          />
          <DetailField label="Views" value={facility.viewCount || 0} />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      {/* Facility Header */}
      <div className="mb-8">
        {/* Title and Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {facility.title}
              </h1>
              <SubscribeButton contentId={facility.id} />
            </div>

            {facility.description && (
              <p className="text-xl text-gray-600 mb-4">
                {facility.description}
              </p>
            )}
          </div>

          {/* Availability Status */}
          <div className="flex items-center gap-2 ml-4">
            {isAvailable ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-green-100 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Available</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-red-100 text-red-800">
                <XCircle className="w-4 h-4" />
                <span className="font-medium">Unavailable</span>
              </div>
            )}
          </div>
        </div>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>
              Added by{" "}
              {facility.author?.username ||
                facility.author?.email ||
                "Anonymous"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Created {formatDate(facility.createdAt)}</span>
          </div>

          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>{facility.location?.name}</span>
          </div>

          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span>{facility.viewCount || 0} views</span>
          </div>
        </div>
      </div>

      {/* Facility Content */}
      <div className="space-y-6">
        {/* Category and Type */}
        <div className="flex flex-wrap gap-3 mb-6">
          <span
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${getCategoryColor(
              facility.category,
            )}`}
          >
            {getCategoryIcon(facility.category)}
            <span className="font-medium">{facility.category}</span>
          </span>

          <span
            className={`inline-flex items-center px-3 py-2 rounded-lg border ${getTypeColor(
              facility.metadata?.facilityType,
            )}`}
          >
            <span className="font-medium">
              {facility.metadata?.facilityType === "available" && "Available"}
              {facility.metadata?.facilityType === "reservable" && "Reservable"}
              {facility.metadata?.facilityType === "walkin" && "Walk-in Only"}
            </span>
          </span>

          {facility.metadata?.reservationRequired && (
            <span className="inline-flex items-center px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-800">
              <Calendar className="w-4 h-4 mr-1" />
              Reservation Required
            </span>
          )}
        </div>

        {/* Detailed Content */}
        {facility.contentBody && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Details
            </h3>
            <div className="prose prose-gray max-w-none">
              <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                {facility.contentBody}
              </p>
            </div>
          </div>
        )}

        {/* Tags */}
        {facility.tags && facility.tags.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {facility.tags.map((tag: string, index: number) => (
                <span
                  key={index}
                  className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sidebar Information */}
      <div className="space-y-6">
        {/* Quick Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Info
          </h3>
          <div className="space-y-3 text-sm">
            {facility.metadata?.facilityLocation && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Location</p>
                  <p className="font-medium text-gray-900">
                    {facility.metadata.facilityLocation}
                  </p>
                </div>
              </div>
            )}

            {facility.metadata?.operatingHours && (
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Operating Hours</p>
                  <p className="font-medium text-gray-900">
                    {facility.metadata.operatingHours}
                  </p>
                </div>
              </div>
            )}

            {facility.metadata?.contact && (
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Contact</p>
                  <p className="font-medium text-gray-900">
                    {facility.metadata.contact}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Access Type</p>
                <p className="font-medium text-gray-900">
                  {facility.metadata?.facilityType === "available" &&
                    "Walk-in Available"}
                  {facility.metadata?.facilityType === "reservable" &&
                    "Advance Reservation"}
                  {facility.metadata?.facilityType === "walkin" &&
                    "First Come, First Served"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        {facility.metadata?.facilityType === "reservable" && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <button className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors">
              <Calendar className="w-4 h-4" />
              Make Reservation
            </button>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Reserve this facility for your needs
            </p>
          </div>
        )}

        {/* Additional Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Additional Info
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Visibility</span>
              <span className="font-medium text-gray-900 capitalize">
                {facility.visibility.toLowerCase()}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Last Updated</span>
              <span className="font-medium text-gray-900">
                {formatDate(facility.updatedAt || facility.createdAt)}
              </span>
            </div>

            {facility.expiryDate && (
              <div className="flex justify-between">
                <span className="text-gray-600">Expires</span>
                <span className="font-medium text-gray-900">
                  {formatDate(facility.expiryDate)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </IndividualViewPageLayout>
  );
}
